import { useCallback, useMemo, useState } from 'react'
import { NavLink, Route, Routes, Navigate } from 'react-router-dom'
import { Check, CircleSlash, Play, ServerCrash, ShieldCheck, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  FOUR_EYES_LIVE,
  fetchFourEyes,
  approveFourEyes,
  rejectFourEyes,
  cancelFourEyes,
  executeFourEyes,
  type FourEyesRow,
} from '@/lib/adapters'
import { useToast } from '@/providers/UiProviders'
import { PERMISSIONS } from '@/lib/permissions'
import { EmptyState, PermissionDenied } from '@/components/States'
import { PlannedBadge } from '@/components/DataDisplay'
import { useAuth } from '@/providers/AuthProvider'
import { AuditTable } from './AuditScreen'
import { AuditSearch } from './AuditSearch'
import { PIIAccessLog } from './PIIAccessLog'
import { StaffActivity } from './StaffActivity'
import { SuspiciousStaff } from './SuspiciousStaff'

/**
 * A18 — Xavfsizlik.
 *
 *   A18.01 Audit jurnali            — ISHLAYDI (jonli `admin_audit`)
 *   A18.06 Four-eyes so'rovlari     — sxema tayyor, endpoint YO'Q (quyida)
 *
 * Qolgan A18 ekranlari (A18.02–.20) Faza 1 dan keyin.
 */

function Tabs() {
  const t = useT()
  const tabs = [
    { to: '/security/audit', label: t.nav.auditLog, id: 'A18.01' },
    { to: '/security/audit-search', label: t.auditSearch.title, id: 'A18.02' },
    { to: '/security/pii-access', label: 'PII kirish', id: 'A18.03' },
    { to: '/security/staff', label: t.security.staffActivity, id: 'A18.04' },
    { to: '/security/suspicious', label: t.suspStaff.tab, id: 'A18.05' },
    { to: '/security/four-eyes', label: t.fourEyes.tab, id: 'A18.06' },
  ]
  return (
    <div className="flex items-center gap-1 border-b border-border-subtle px-3">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-base transition-colors',
              isActive
                ? 'border-accent font-medium text-primary'
                : 'border-transparent text-secondary hover:text-primary',
            )
          }
        >
          {tab.label}
          <span className="font-mono text-xs text-tertiary">{tab.id}</span>
        </NavLink>
      ))}
    </div>
  )
}

/* ── A18.06 Four-eyes so'rovlari navbati ──────────────────────── */

function FourEyesQueue() {
  const t = useT()

  // Ustunlar migratsiya 000076 ning ustunlariga bir-bir mos keladi.
  // Ular hozir chizilmasa ham KONTRAKT: A endpointni shu shaklda yozadi.
  const columns = useMemo<AdminColumn<FourEyesRow>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 140 },
      { key: 'requester', header: t.fourEyes.requester, type: 'text', width: 150 },
      { key: 'action', header: t.col.action, type: 'text', width: 170 },
      { key: 'target', header: t.col.target, type: 'id', minWidth: 150 },
      {
        key: 'danger_level',
        header: t.fourEyes.level,
        type: 'enum',
        width: 110,
        align: 'center',
        options: [
          { value: '4', label: `4 · ${t.danger.levelName[4]}`, status: 'banned' },
          { value: '5', label: `5 · ${t.danger.levelName[5]}`, status: 'blocked' },
        ],
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 150,
        options: [
          { value: 'pending', label: t.status.pending, status: 'pending' },
          { value: 'approved', label: t.status.approved, status: 'approved' },
          { value: 'rejected', label: t.status.rejected, status: 'rejected' },
          { value: 'executed', label: t.status.executed, status: 'executed' },
          { value: 'expired', label: t.status.expired, status: 'expired' },
          { value: 'cancelled', label: t.status.cancelled, status: 'cancelled' },
        ],
      },
      { key: 'approver', header: t.fourEyes.approver, type: 'text', width: 150 },
      { key: 'required_role', header: t.fourEyes.requiredRole, type: 'text', width: 140 },
      { key: 'reason', header: t.common.reason, type: 'longtext', minWidth: 220 },
      { key: 'expires_at', header: t.fourEyes.expires, type: 'date', width: 140, hiddenByDefault: true },
      { key: 'execute_after', header: t.fourEyes.executeAfter, type: 'date', width: 140, hiddenByDefault: true },
      { key: 'impact', header: t.fourEyes.impact, type: 'json', width: 120, hiddenByDefault: true },
    ],
    [t],
  )

  const fetcher = useCallback(fetchFourEyes, [])
  const { toast } = useToast()
  const { identity } = useAuth()
  // DataTable o'z ma'lumotini boshqaradi; amaldan keyin qayta yuklash uchun
  // `key` ni o'zgartirib remount qilamiz (imperativ refresh propи yo'q).
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.fourEyes.actionDone, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        // Server qoidasi rad etsa (self-approve 403, kutish davri 409, ...) —
        // xabar KO'RSATILADI, yashirilmaydi.
        toast(t.fourEyes.actionFailed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
      }
    },
    [toast, t],
  )

  const rowActions = useMemo<RowAction<FourEyesRow>[]>(
    () => [
      {
        id: 'approve',
        label: t.fourEyes.approve,
        icon: <Check size={13} />,
        visible: (r) => r.status === 'pending',
        onSelect: (r) => void run(() => approveFourEyes(r.id)),
      },
      {
        id: 'reject',
        label: t.fourEyes.reject,
        icon: <X size={13} />,
        danger: true,
        visible: (r) => r.status === 'pending',
        onSelect: (r) => void run(() => rejectFourEyes(r.id)),
      },
      {
        id: 'execute',
        label: t.fourEyes.execute,
        icon: <Play size={13} />,
        // Faqat SO'ROVCHI bajaradi (server ham majburlaydi) — begonaga ko'rsatilmaydi.
        visible: (r) => r.status === 'approved' && r.requester === identity?.username,
        onSelect: (r) => void run(() => executeFourEyes(r.id)),
      },
      {
        id: 'cancel',
        label: t.fourEyes.cancel,
        icon: <CircleSlash size={13} />,
        danger: true,
        visible: (r) => r.status === 'pending' || r.status === 'approved',
        onSelect: (r) => {
          // Sabab ≥10 belgi (server ham talab qiladi). Vaqtinchalik prompt —
          // to'liq sabab-dialogi follow-up (grom-admin-design §7).
          const reason = (window.prompt(t.fourEyes.cancelPrompt) ?? '').trim()
          if (reason.length < 10) {
            if (reason.length > 0) toast(t.fourEyes.reasonShort, { tone: 'warning' })
            return
          }
          void run(() => cancelFourEyes(r.id, reason))
        },
      },
    ],
    [t, run, identity, toast],
  )

  // Endpoint yo'q → navbat SOXTA ma'lumot bilan to'ldirilmaydi.
  // C-1 dagi A23.01 bilan bir xil naqsh: nima yetishmayotgani ochiq aytiladi.
  if (!FOUR_EYES_LIVE) {
    return (
      <EmptyState
        icon={<ServerCrash size={28} strokeWidth={1.5} />}
        title={t.fourEyes.noEndpointTitle}
        description={t.fourEyes.noEndpointDesc}
        action={
          <div className="max-w-lg space-y-2 rounded-md border border-dashed border-border-default p-3 text-left">
            <div className="flex items-center gap-2">
              <PlannedBadge />
              <span className="text-sm text-secondary">A yo'lidan kutilmoqda</span>
            </div>
            <ul className="space-y-1 font-mono text-xs text-tertiary">
              <li>GET&nbsp;&nbsp;&nbsp;/admin/four-eyes → {'{items, total}'}</li>
              <li>POST&nbsp;&nbsp;/admin/four-eyes → so‘rov yaratish</li>
              <li>POST&nbsp;&nbsp;/admin/four-eyes/:id/approve</li>
              <li>POST&nbsp;&nbsp;/admin/four-eyes/:id/reject</li>
              <li>POST&nbsp;&nbsp;/admin/four-eyes/:id/cancel</li>
              <li>POST&nbsp;&nbsp;/admin/four-eyes/:id/execute</li>
            </ul>
            <p className="text-xs text-tertiary">
              Jadval sxemasi tayyor:{' '}
              <span className="font-mono">migrations/000076_four_eyes_requests.up.sql</span>
              <br />
              Kontrakt va qoidalar: <span className="font-mono">tz/admin/HOLAT.md</span> §2
            </p>
            {/* Nazorat DB darajasida — server buni chetlab o'tolmaydi. */}
            <ul className="space-y-0.5 border-t border-border-subtle pt-2 text-xs text-tertiary">
              <li>· {t.fourEyes.selfApprovalBlocked} — <span className="font-mono">ck_fe_no_self_approval</span></li>
              <li>· {t.fourEyes.executorIsRequester} — <span className="font-mono">ck_fe_executor_is_requester</span></li>
            </ul>
          </div>
        }
      />
    )
  }

  return (
    <DataTable<FourEyesRow>
      key={reloadKey}
      screenId="A18.06"
      permission={PERMISSIONS.FOUR_EYES_VIEW}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      exportable
      rowActions={rowActions}
      emptyState={{ icon: <ShieldCheck size={28} strokeWidth={1.5} /> }}
    />
  )
}

/* ── Kirish nuqtasi ───────────────────────────────────────────── */

export function SecurityScreen() {
  const t = useT()
  const { can } = useAuth()

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      {/* Tavsif ATAYLAB yo'q: u tabga bog'liq bo'lardi, sarlavha esa ikkala
          tab uchun umumiy. Har tab o'zini o'zi tushuntiradi. */}
      <PageHeader title={t.nav.security} screenId="A18" />
      <Tabs />
      <div className="flex min-h-0 flex-1 flex-col">
        <Routes>
          <Route index element={<Navigate to="audit" replace />} />
          <Route path="audit" element={<AuditTable />} />
          <Route path="audit-search" element={<AuditSearch />} />
          <Route path="pii-access" element={<PIIAccessLog />} />
          <Route path="staff" element={<StaffActivity />} />
          <Route path="suspicious" element={<SuspiciousStaff />} />
          <Route path="four-eyes" element={<FourEyesQueue />} />
        </Routes>
      </div>
    </>
  )
}
